import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, MenuItem, FormControl, InputLabel, Select, Alert,
  Stepper, Step, StepLabel, Divider, CircularProgress, Tabs, Tab
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import { COLORS } from '../../theme/constants';

const { primary: PRIMARY, teal: TEAL } = COLORS;

const BANCOS_CHILE = [
  'Banco Estado',
  'Banco de Chile',
  'Banco Santander',
  'Banco BCI',
  'Banco Itaú',
  'Scotiabank',
  'Banco Security',
  'Banco Falabella',
  'Banco Consorcio',
  'Banco Ripley',
  'Banco Internacional',
  'Banco Edwards Citi',
  'Banco Paris',
  'Coopeuch',
  'Los Héroes',
  'Other',
];

const TIPOS_CUENTA = ['Cuenta Corriente', 'Cuenta Vista', 'Cuenta Rut'];

const MOTIVOS = [
  'Paciente Fallecido',
  'Prestaciones no realizadas',
  'Cambio en el tratamiento',
  'Error en el cobro',
  'Error en el presupuesto',
];

const steps = ['Prestaciones a devolver', 'Motivo', 'Datos de transferencia', 'Revisión'];

function fmtDate(v) {
  if (!v) return '—';
  const d = typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
  return d.toLocaleDateString('es-CL');
}

function limpiaRutDv(rut) {
  if (!rut) return '';
  const cleaned = String(rut).replace(/\./g, '').replace(/-/g, '');
  return cleaned.length > 1 ? cleaned.slice(0, -1) : cleaned;
}

function statusColor(status) {
  if (status === 'aprobada') return { color: '#047857', bg: '#D1FAE5' };
  if (status === 'rechazada') return { color: '#991B1B', bg: '#FEE2E2' };
  return { color: '#92400E', bg: '#FEF3C7' }; // solicitada u otros
}

export default function Devoluciones() {
  const { currentUser, userData } = useAuth();
  const { activeBranch, filterByBranch } = useBranch();

  const [tab, setTab] = useState(0);
  const [sales, setSales] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingSales, setLoadingSales] = useState(false);
  const [loadingRefunds, setLoadingRefunds] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [reason, setReason] = useState('');
  const [transfer, setTransfer] = useState({
    rut: '',
    name: '',
    bank: '',
    accountType: '',
    accountNumber: '',
  });
  const [success, setSuccess] = useState(false);
  const [summaryRefund, setSummaryRefund] = useState(null);

  useEffect(() => {
    const loadSales = async () => {
      setLoadingSales(true);
      try {
        const snap = await getDocs(collection(db, 'Sales'));
        let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list = filterByBranch(list, { includeUnassigned: true });
        setSales(list);
      } catch (e) {
        console.error('Error cargando ventas:', e);
      } finally {
        setLoadingSales(false);
      }
    };
    loadSales();
  }, [filterByBranch]);

  useEffect(() => {
    if (tab !== 1) return;
    const loadRefunds = async () => {
      setLoadingRefunds(true);
      try {
        const snap = await getDocs(collection(db, 'refunds'));
        let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list = filterByBranch(list, { includeUnassigned: true });
        setRefunds(list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
      } catch (e) {
        console.error('Error cargando solicitudes:', e);
      } finally {
        setLoadingRefunds(false);
      }
    };
    loadRefunds();
  }, [tab, filterByBranch]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return sales.filter(s =>
      (s.boletaNumber || '').toLowerCase().includes(q) ||
      (s.presupuestoNumber || '').toLowerCase().includes(q) ||
      (s.patientName || '').toLowerCase().includes(q) ||
      (s.ownerRut || '').toLowerCase().includes(q) ||
      (s.ownerName || '').toLowerCase().includes(q)
    );
  }, [sales, search]);

  const openRefund = (sale) => {
    setSelectedSale(sale);
    setSelectedItems((sale.items || []).map(it => ({
      ...it,
      refundQty: 0,
      maxQty: Number(it.cantidad) || 1,
      unitPrice: (Number(it.total) || 0) / (Number(it.cantidad) || 1),
    })));
    setReason('');
    setTransfer({
      rut: sale.ownerRut || '',
      name: sale.ownerName || '',
      bank: '',
      accountType: '',
      accountNumber: '',
    });
    setActiveStep(0);
    setSuccess(false);
    setOpen(true);
  };

  const totalRefund = useMemo(() => {
    return selectedItems.reduce((sum, it) => {
      const qty = Number(it.refundQty) || 0;
      return sum + (it.unitPrice * qty);
    }, 0);
  }, [selectedItems]);

  const hasSelection = selectedItems.some(it => (Number(it.refundQty) || 0) > 0);

  useEffect(() => {
    if (transfer.bank === 'Banco Estado' && transfer.accountType === 'Cuenta Rut') {
      const base = limpiaRutDv(transfer.rut || selectedSale?.ownerRut);
      setTransfer(t => ({ ...t, accountNumber: base }));
    }
  }, [transfer.bank, transfer.accountType, transfer.rut, selectedSale]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const itemsToRefund = selectedItems
        .filter(it => (Number(it.refundQty) || 0) > 0)
        .map(it => ({
          descripcion: it.descripcion || it.prestacion || '—',
          cantidadPagada: Number(it.cantidad) || 1,
          cantidadDevolver: Number(it.refundQty),
          precioUnitario: it.unitPrice,
          totalDevolver: it.unitPrice * Number(it.refundQty),
        }));

      await addDoc(collection(db, 'refunds'), {
        saleId: selectedSale.id,
        boletaNumber: selectedSale.boletaNumber || '',
        presupuestoNumber: selectedSale.presupuestoNumber || '',
        patientName: selectedSale.patientName || '',
        ownerRut: selectedSale.ownerRut || '',
        ownerName: selectedSale.ownerName || '',
        branchId: activeBranch?.id || selectedSale.branchId || '',
        items: itemsToRefund,
        totalRefund,
        reason,
        transfer,
        status: 'solicitada',
        createdAt: serverTimestamp(),
        createdBy: currentUser?.uid || '',
        createdByName: userData?.name || currentUser?.email || '',
      });
      setSuccess(true);
    } catch (e) {
      console.error('Error guardando devolución:', e);
      alert('Error al guardar la solicitud: ' + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    if (activeStep === 0) {
      return (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} color={PRIMARY} mb={1}>
            Venta: {selectedSale?.boletaNumber || selectedSale?.presupuestoNumber || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Paciente: {selectedSale?.patientName || '—'} · Tutor: {selectedSale?.ownerName || '—'} · RUT: {selectedSale?.ownerRut || '—'}
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="right">Cantidad pagada</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Cantidad a devolver</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedItems.map((it, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{it.descripcion || it.prestacion || '—'}</TableCell>
                    <TableCell align="right">{it.cantidad || 1}</TableCell>
                    <TableCell align="right">${Math.round(Number(it.total) || 0).toLocaleString('es-CL')}</TableCell>
                    <TableCell align="right" sx={{ width: 120 }}>
                      <TextField
                        type="number"
                        size="small"
                        inputProps={{ min: 0, max: it.maxQty, style: { textAlign: 'right' } }}
                        value={it.refundQty}
                        onChange={(e) => {
                          const v = Math.max(0, Math.min(it.maxQty, Number(e.target.value) || 0));
                          setSelectedItems(prev => prev.map((p, i) => i === idx ? { ...p, refundQty: v } : p));
                        }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell align="right">${Math.round(it.unitPrice * (Number(it.refundQty) || 0)).toLocaleString('es-CL')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="h6" fontWeight={700} color={TEAL} textAlign="right">
            Total a devolver: ${Math.round(totalRefund).toLocaleString('es-CL')}
          </Typography>
        </Box>
      );
    }

    if (activeStep === 1) {
      return (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} color={PRIMARY} mb={2}>
            Seleccione el motivo de la devolución
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="refund-reason-label">Motivo *</InputLabel>
            <Select
              labelId="refund-reason-label"
              value={reason}
              label="Motivo *"
              onChange={(e) => setReason(e.target.value)}
              size="small"
            >
              {MOTIVOS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ mt: 2, p: 2, bgcolor: '#F8FAFC', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Monto a devolver: <strong>${Math.round(totalRefund).toLocaleString('es-CL')}</strong>
            </Typography>
          </Box>
        </Box>
      );
    }

    if (activeStep === 2) {
      return (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} color={PRIMARY} mb={2}>
            Datos de transferencia del tutor
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                size="small"
                fullWidth
                label="RUT titular"
                value={transfer.rut}
                onChange={(e) => setTransfer({ ...transfer, rut: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                size="small"
                fullWidth
                label="Nombre titular"
                value={transfer.name}
                onChange={(e) => setTransfer({ ...transfer, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="bank-label">Banco</InputLabel>
                <Select
                  labelId="bank-label"
                  value={transfer.bank}
                  label="Banco"
                  onChange={(e) => setTransfer({ ...transfer, bank: e.target.value, accountNumber: '' })}
                >
                  {BANCOS_CHILE.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="account-type-label">Tipo de cuenta</InputLabel>
                <Select
                  labelId="account-type-label"
                  value={transfer.accountType}
                  label="Tipo de cuenta"
                  onChange={(e) => setTransfer({ ...transfer, accountType: e.target.value, accountNumber: '' })}
                >
                  {TIPOS_CUENTA.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                size="small"
                fullWidth
                label="Número de cuenta"
                value={transfer.accountNumber}
                onChange={(e) => setTransfer({ ...transfer, accountNumber: e.target.value })}
                disabled={transfer.bank === 'Banco Estado' && transfer.accountType === 'Cuenta Rut'}
              />
            </Grid>
          </Grid>
          {transfer.bank === 'Banco Estado' && transfer.accountType === 'Cuenta Rut' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              El número de cuenta se completó automáticamente con el RUT sin guion ni dígito verificador.
            </Alert>
          )}
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="subtitle2" fontWeight={700} color={PRIMARY} mb={2}>
          Revise la solicitud antes de ingresarla
        </Typography>
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #E2E8F0', borderRadius: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Venta:</strong> {selectedSale?.boletaNumber || selectedSale?.presupuestoNumber || '—'}<br />
            <strong>Paciente:</strong> {selectedSale?.patientName || '—'}<br />
            <strong>Tutor:</strong> {selectedSale?.ownerName || '—'} ({selectedSale?.ownerRut || '—'})
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="body2" fontWeight={700} gutterBottom>Prestaciones a devolver:</Typography>
          {selectedItems.filter(it => (Number(it.refundQty) || 0) > 0).map((it, i) => (
            <Typography key={i} variant="body2" color="text.secondary">
              · {it.descripcion || it.prestacion || '—'}: {it.refundQty} x ${Math.round(it.unitPrice).toLocaleString('es-CL')}
            </Typography>
          ))}
          <Typography variant="h6" fontWeight={700} color={TEAL} textAlign="right" mt={1}>
            Total: ${Math.round(totalRefund).toLocaleString('es-CL')}
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Motivo:</strong> {reason}<br />
            <strong>Titular:</strong> {transfer.name} ({transfer.rut})<br />
            <strong>Banco:</strong> {transfer.bank} · <strong>Tipo:</strong> {transfer.accountType}<br />
            <strong>Cuenta:</strong> {transfer.accountNumber}
          </Typography>
        </Paper>
      </Box>
    );
  };

  const isStepValid = () => {
    if (activeStep === 0) return hasSelection;
    if (activeStep === 1) return !!reason;
    if (activeStep === 2) return transfer.rut && transfer.name && transfer.bank && transfer.accountType && transfer.accountNumber;
    return true;
  };

  const renderSummary = (r) => (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        <strong>Venta:</strong> {r.boletaNumber || r.presupuestoNumber || '—'}<br />
        <strong>Paciente:</strong> {r.patientName || '—'}<br />
        <strong>Tutor:</strong> {r.ownerName || '—'} ({r.ownerRut || '—'})
      </Typography>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="body2" fontWeight={700} gutterBottom>Prestaciones a devolver:</Typography>
      {(r.items || []).map((it, i) => (
        <Typography key={i} variant="body2" color="text.secondary">
          · {it.descripcion || '—'}: {it.cantidadDevolver} x ${Math.round(it.precioUnitario).toLocaleString('es-CL')}
        </Typography>
      ))}
      <Typography variant="h6" fontWeight={700} color={TEAL} textAlign="right" mt={1}>
        Total: ${Math.round(r.totalRefund || 0).toLocaleString('es-CL')}
      </Typography>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="body2" color="text.secondary" gutterBottom>
        <strong>Motivo:</strong> {r.reason}<br />
        <strong>Titular:</strong> {r.transfer?.name} ({r.transfer?.rut})<br />
        <strong>Banco:</strong> {r.transfer?.bank} · <strong>Tipo:</strong> {r.transfer?.accountType}<br />
        <strong>Cuenta:</strong> {r.transfer?.accountNumber}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 3, bgcolor: '#F4F6F8', minHeight: '100vh' }}>
      <Typography variant="h4" fontWeight={700} color={PRIMARY} mb={2}>Devoluciones</Typography>

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Nueva devolución" />
          <Tab label="Solicitudes ingresadas" />
        </Tabs>
      </Paper>

      {tab === 0 ? (
        <>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                fullWidth
                label="Buscar venta (boleta, presupuesto, paciente, RUT o nombre del tutor)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && setSearch(e.target.value)}
              />
              <Button variant="contained" startIcon={<SearchIcon />} onClick={() => setSearch(search)}>
                Buscar
              </Button>
            </Box>
          </Paper>

          {loadingSales ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : search && filtered.length === 0 ? (
            <Alert severity="info">No se encontraron ventas con el criterio ingresado.</Alert>
          ) : filtered.length > 0 ? (
            <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid #E2E8F0' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Boleta</TableCell>
                    <TableCell>Presupuesto</TableCell>
                    <TableCell>Paciente</TableCell>
                    <TableCell>RUT tutor</TableCell>
                    <TableCell>Tutor</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map(s => (
                    <TableRow key={s.id} hover>
                      <TableCell>{s.boletaNumber || '—'}</TableCell>
                      <TableCell>{s.presupuestoNumber || '—'}</TableCell>
                      <TableCell>{s.patientName || '—'}</TableCell>
                      <TableCell>{s.ownerRut || '—'}</TableCell>
                      <TableCell>{s.ownerName || '—'}</TableCell>
                      <TableCell>{fmtDate(s.createdAt)}</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => openRefund(s)}>Devolver</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </>
      ) : (
        <>
          {loadingRefunds ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : refunds.length === 0 ? (
            <Alert severity="info">No hay solicitudes ingresadas.</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid #E2E8F0' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Boleta</TableCell>
                    <TableCell>Presupuesto</TableCell>
                    <TableCell>Paciente</TableCell>
                    <TableCell>Tutor</TableCell>
                    <TableCell>Monto</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Fecha</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {refunds.map(r => {
                    const st = statusColor(r.status);
                    return (
                      <TableRow
                        key={r.id}
                        hover
                        onClick={() => setSummaryRefund(r)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{r.boletaNumber || '—'}</TableCell>
                        <TableCell>{r.presupuestoNumber || '—'}</TableCell>
                        <TableCell>{r.patientName || '—'}</TableCell>
                        <TableCell>{r.ownerName || '—'}</TableCell>
                        <TableCell>${Math.round(r.totalRefund || 0).toLocaleString('es-CL')}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'inline-block', px: 1, py: 0.5, borderRadius: 1, bgcolor: st.bg, color: st.color, fontSize: '0.75rem', fontWeight: 700 }}>
                            {r.status || 'solicitada'}
                          </Box>
                        </TableCell>
                        <TableCell>{fmtDate(r.createdAt)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {success ? 'Solicitud ingresada' : 'Solicitud de devolución'}
        </DialogTitle>
        <DialogContent>
          {success ? (
            <Alert severity="success">
              La solicitud de devolución fue ingresada exitosamente y quedó en estado <strong>solicitada</strong>.
            </Alert>
          ) : (
            <>
              <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
              </Stepper>
              {renderStep()}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cerrar</Button>
          {!success && activeStep > 0 && (
            <Button onClick={() => setActiveStep(activeStep - 1)}>Atrás</Button>
          )}
          {!success && activeStep < steps.length - 1 && (
            <Button variant="contained" disabled={!isStepValid()} onClick={() => setActiveStep(activeStep + 1)}>
              Siguiente
            </Button>
          )}
          {!success && activeStep === steps.length - 1 && (
            <Button variant="contained" color="error" disabled={saving} onClick={handleSubmit}>
              {saving ? 'Guardando…' : 'Ingresar solicitud'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={!!summaryRefund} onClose={() => setSummaryRefund(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Resumen de solicitud</DialogTitle>
        <DialogContent>
          {summaryRefund && renderSummary(summaryRefund)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSummaryRefund(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
